import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { AvatarPlaceholderPng, IconSendGiftSvg } from '../../../../assets/friends';
import { ReferralInvitee } from '../../../../services/api';
import { goldGlow } from '../../../../shared/glowStyles';
import { getAvatarUrl } from '../../../../utils/axiosInstance';
import { useSvgId } from '../../../../utils/hooks/useSvgId';
import { Pressable } from '../../common/Touchable';
import {
	FRIENDS_FONT,
	friendsColors,
	hairline,
	inset,
	RADIUS_LG,
	RADIUS_MD,
} from '../friendsTheme';
import { getInviteScreenCopy, lastSeenLabel } from './inviteFriendsCatalog';
import { CLAIM_FILL, CLAIM_TEXT, PROGRESS_FILL, PROGRESS_TRACK } from './inviteFriendsTheme';

/**
 * One row of "Invited Players", laid out as the Notion preview draws it and painted in the friends
 * tab's own tokens (`friendsTheme`): the same raised card and hairline as a `FriendRow`, a smaller
 * avatar, the username over an activity line, the "Level n/5" progress toward the reward, and the
 * gift control - gold with a Claim button once the reward is available, muted before that.
 */
const ROW = { width: 334, paddingX: 8, paddingY: 8, gap: 6 };
const AVATAR = 36;
// Fixed columns so the Level bars line up whether or not a row carries a Claim button.
const IDENTITY_WIDTH = 104;
const NAME_SIZE = 14;
const STATUS_SIZE = 11;
const DOT = 8;
const LEVEL_SIZE = 11;
const BAR_HEIGHT = 6;
const GIFT_BUTTON = 28;
const GIFT_ICON = { width: 13, height: 15 };
const CLAIM = { width: 56, height: 28, label: 12 };
const ACTIONS_GAP = 4;
const ACTIONS_WIDTH = GIFT_BUTTON + ACTIONS_GAP + CLAIM.width;

type Props = {
	invitee: ReferralInvitee;
	scale: number;
	claiming: boolean;
	onClaim: (invitee: ReferralInvitee) => void;
};

export const InvitedPlayerRow = ({ invitee, scale, claiming, onClaim }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const copy = useMemo(() => getInviteScreenCopy(t), [t]);
	const barId = useSvgId('invitedProgress');
	const claimId = useSvgId('invitedClaim');

	const progress = Math.min(1, invitee.level / Math.max(1, invitee.requiredLevel));
	const complete = progress >= 1;
	const available = invitee.rewardStatus === 'AVAILABLE';
	const claimed = invitee.rewardStatus === 'CLAIMED';
	const avatar = invitee.avatarFileId
		? { uri: getAvatarUrl(invitee.avatarFileId) }
		: AvatarPlaceholderPng;

	return (
		<View style={styles.row}>
			<Image source={avatar} style={styles.avatar} />

			<View style={styles.identity}>
				<Text numberOfLines={1} style={styles.name}>
					{invitee.username ?? '—'}
				</Text>
				<View style={styles.statusLine}>
					<View
						style={[
							styles.dot,
							{ backgroundColor: invitee.isOnline ? friendsColors.online : friendsColors.offline },
						]}
					/>
					<Text
						numberOfLines={1}
						style={[
							styles.status,
							{ color: invitee.isOnline ? friendsColors.online : friendsColors.muted },
						]}
					>
						{invitee.isOnline ? copy.active : lastSeenLabel(t, invitee.lastSeenAt)}
					</Text>
				</View>
			</View>

			<View style={styles.progress}>
				<Text numberOfLines={1} style={styles.level}>
					{copy.level(invitee.level, invitee.requiredLevel)}
				</Text>
				<View style={styles.track}>
					<View style={[styles.fill, { width: `${progress * 100}%` }]}>
						<Svg width='100%' height='100%'>
							<Defs>
								<LinearGradient id={barId} x1='0' y1='0' x2='1' y2='0'>
									{(complete ? PROGRESS_FILL.complete : PROGRESS_FILL.progress).map(
										([offset, color]) => (
											<Stop key={offset} offset={offset} stopColor={color} />
										),
									)}
								</LinearGradient>
							</Defs>
							<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${barId})`} />
						</Svg>
					</View>
				</View>
			</View>

			<View style={styles.actions}>
				<View style={[styles.giftButton, available && styles.giftButtonActive]}>
					<View style={{ opacity: available || claimed ? 1 : 0.4 }}>
						<IconSendGiftSvg width={GIFT_ICON.width * scale} height={GIFT_ICON.height * scale} />
					</View>
				</View>

				{available ? (
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={`${copy.claim} ${invitee.username ?? ''}`}
						disabled={claiming}
						onPress={() => onClaim(invitee)}
						style={styles.claim}
					>
						<View pointerEvents='none' style={styles.claimPlate}>
							<Svg width='100%' height='100%'>
								<Defs>
									<LinearGradient id={claimId} x1='0' y1='0' x2='0' y2='1'>
										{CLAIM_FILL.map(([offset, color]) => (
											<Stop key={offset} offset={offset} stopColor={color} />
										))}
									</LinearGradient>
								</Defs>
								<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${claimId})`} />
							</Svg>
						</View>
						{claiming ? (
							<ActivityIndicator size='small' color={CLAIM_TEXT} />
						) : (
							<Text style={styles.claimLabel}>{copy.claim}</Text>
						)}
					</Pressable>
				) : claimed ? (
					<Text style={styles.claimedLabel}>{copy.claimed}</Text>
				) : null}
			</View>
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		row: {
			width: ROW.width * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: ROW.gap * scale,
			paddingHorizontal: inset(ROW.paddingX, scale),
			paddingVertical: inset(ROW.paddingY, scale),
			borderRadius: RADIUS_LG * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.border,
			backgroundColor: friendsColors.surface,
		},
		avatar: {
			width: AVATAR * scale,
			height: AVATAR * scale,
			borderRadius: (AVATAR / 2) * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
		},
		identity: {
			width: IDENTITY_WIDTH * scale,
			gap: 3 * scale,
		},
		name: {
			color: friendsColors.gold50,
			fontFamily: FRIENDS_FONT,
			fontSize: NAME_SIZE * scale,
			fontWeight: '700',
			lineHeight: NAME_SIZE * 1.2 * scale,
		},
		statusLine: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 4 * scale,
		},
		dot: {
			width: DOT * scale,
			height: DOT * scale,
			borderRadius: (DOT / 2) * scale,
		},
		status: {
			flexShrink: 1,
			fontFamily: FRIENDS_FONT,
			fontSize: STATUS_SIZE * scale,
			fontWeight: '500',
			lineHeight: STATUS_SIZE * 1.3 * scale,
		},
		progress: {
			flex: 1,
			gap: 4 * scale,
		},
		level: {
			color: friendsColors.inGame,
			fontFamily: FRIENDS_FONT,
			fontSize: LEVEL_SIZE * scale,
			fontWeight: '700',
			lineHeight: LEVEL_SIZE * 1.3 * scale,
		},
		track: {
			height: BAR_HEIGHT * scale,
			borderRadius: (BAR_HEIGHT / 2) * scale,
			backgroundColor: PROGRESS_TRACK,
			overflow: 'hidden',
		},
		fill: {
			height: '100%',
			borderRadius: (BAR_HEIGHT / 2) * scale,
			overflow: 'hidden',
		},
		actions: {
			width: ACTIONS_WIDTH * scale,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'flex-end',
			gap: ACTIONS_GAP * scale,
		},
		giftButton: {
			width: GIFT_BUTTON * scale,
			height: GIFT_BUTTON * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.border,
			backgroundColor: friendsColors.surfaceSunken,
		},
		giftButtonActive: {
			borderColor: friendsColors.borderGold,
		},
		claim: {
			width: CLAIM.width * scale,
			height: CLAIM.height * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.gold50,
			...goldGlow,
		},
		claimPlate: {
			...StyleSheet.absoluteFillObject,
			borderRadius: RADIUS_MD * scale,
			overflow: 'hidden',
		},
		claimLabel: {
			color: CLAIM_TEXT,
			fontFamily: FRIENDS_FONT,
			fontSize: CLAIM.label * scale,
			fontWeight: '900',
			lineHeight: CLAIM.label * scale,
		},
		claimedLabel: {
			minWidth: CLAIM.width * scale,
			textAlign: 'center',
			color: friendsColors.muted,
			fontFamily: FRIENDS_FONT,
			fontSize: CLAIM.label * scale,
			fontWeight: '700',
		},
	});

export default InvitedPlayerRow;
